import { ScriptableUiHighlightWrapper, WithHW } from "@famiprog-foundation/scriptable-ui";
import _ from "lodash";
import { DummyToRememberPeopleToCast, EntityDescriptor, EntityEditorFormSimple, FieldDescriptor, Utils } from "@crispico/foundation-react";
import { EntityFilterMode } from "@crispico/foundation-react/entity_crud/fieldEditors/FilterFieldEditor";
import { Filter } from "@crispico/foundation-react/components/CustomQuery/Filter";
import { FilterOperators } from "@crispico/foundation-gwt-js";
import { SelectExtOption } from "@crispico/foundation-react/components/selectExt/SelectExt";
import { tryJsonParse } from "xopsUtils";
import { GanttAssignmentEntityRequirement } from "../ganttButtons/GanttAssignmentCopyInputData";
import { Label, Segment } from "semantic-ui-react";
import StringFieldRenderer from "@crispico/foundation-react/entity_crud/fieldRenderers/StringFieldRenderer";
import { FieldType } from "@crispico/foundation-react/entity_crud/FieldType";
import { FieldEditorProps, FieldRendererProps } from "@crispico/foundation-react/entity_crud/fieldRenderersEditors";
import { ReactElement, ReactNode } from "react";
import { EntityFieldsFieldEditor } from "@crispico/foundation-react/entity_crud/fieldEditors/EntityFieldsFieldEditor";
import { FieldEditor, IFormValuesHolder, ScriptableUiFieldEditor } from "@crispico/foundation-react/entity_crud/fieldEditors/FieldEditor";
import { EntityNameFieldEditor } from "@crispico/foundation-react/entity_crud/fieldEditors/EntityNameFieldEditor";
import { entityDescriptors } from "@crispico/foundation-react/entity_crud/entityCrudConstants";

// TODO RM38750
const entityRequirementEntityColumns = ["entity", "inputFilters", "importFromDb", "fields", "fieldsToIgnoreForCopyInDb", "optional"];

export class EntityRequirementFieldDescriptor extends FieldDescriptor {

    protected renderFieldEditorInternal(EditorClass: any, props: FieldEditorProps): ReactElement {
        let newProps: any = {
            ...props, onChangeValue: (values: any) => {
                props.onChangeValue?.(values);
                // call submit to update configEntityRequirementsJson field value
                (props.formikProps as any).submitForm();
            }
        }
        return super.renderFieldEditorInternal(EditorClass, newProps);
    }
}

export class EntityRequirementEntityFieldsFieldDescriptor extends EntityRequirementFieldDescriptor {

    protected renderFieldEditorInternal(_: any, props: FieldEditorProps): ReactElement {
        return super.renderFieldEditorInternal(EntityRequirementEntityFieldsFieldEditor, props);
    }

    protected renderFieldInternal(RendererClass: any, props: FieldRendererProps, entity: any): ReactNode {
        return super.renderFieldInternal(RendererClass, { ...props, value: (this.getFieldValue(entity) ?? []).join(',') }, entity);
    }
}

// EntityFieldsFieldEditor keeps the value as a string, but we need it to be an array since the field type is array
class EntityRequirementEntityFieldsFieldEditor extends EntityFieldsFieldEditor {

    protected getValueFromOptions(items: SelectExtOption[]): any {
        return items.map(i => i.value);
    }

    protected getItemsFromValue(value: any): any[] {
        return value ?? [];
    }
}

const ganttAssignmentEntityRequirementEntityDescriptor = new EntityDescriptor({ name: "GanttAssignmentEntityRequirement" })
    .addFieldDescriptor({ name: "entity", type: FieldType.entityName }, new EntityRequirementFieldDescriptor())
    .addFieldDescriptor({ name: "inputFilters", type: FieldType.filter, fieldForEntityName: "entity", mode: EntityFilterMode.OBJECT }, new EntityRequirementFieldDescriptor())
    .addFieldDescriptor({ name: "importFromDb", type: FieldType.boolean }, new EntityRequirementFieldDescriptor())
    .addFieldDescriptor({ name: "fields", type: FieldType.entityFields, fieldForEntityName: "entity" }, new EntityRequirementEntityFieldsFieldDescriptor())
    .addFieldDescriptor({ name: "fieldsToIgnoreForCopyInDb", type: FieldType.entityFields, fieldForEntityName: "entity" }, new EntityRequirementEntityFieldsFieldDescriptor())
    .addFieldDescriptor({ name: "optional", type: FieldType.boolean }, new EntityRequirementFieldDescriptor());


export class GanttAssignmentParams_ConfigEntityRequirementsJsonFieldEditor extends FieldEditor<string, FieldEditorProps> {

    protected getEntityRequirements(): { [key: string]: GanttAssignmentEntityRequirement } {
        const { configEntityRequirementsJson } = this.props.formikProps!.values;
        if (configEntityRequirementsJson) {
            return tryJsonParse(configEntityRequirementsJson) ?? {};
        }
        return {};
    }

    protected updateEntityNames(entityNames: string[]) {
        let newEntityRequirements = {} as any;
        const entityRequirements = this.getEntityRequirements();
        entityNames.forEach((entity) => {
            newEntityRequirements[entity] = entityRequirements[entity] ?? { entity, inputFilters: Filter.createComposed(FilterOperators.forComposedFilter.and, []) };
        })
        this.props.formikProps!.setFieldValue("configEntityRequirementsJson", JSON.stringify(newEntityRequirements));
    }

    protected updateGanttAssignmentEntityRequirement(entityName: string, values: any) {
        let entityRequirements = this.getEntityRequirements();
        if (!values["inputFilters"]) {
            values["inputFilters"] = Filter.createComposed(FilterOperators.forComposedFilter.and, []);
        }
        entityRequirements[entityName] = values;
        this.props.formikProps!.setFieldValue("configEntityRequirementsJson", JSON.stringify(entityRequirements));
    }

    protected getFieldsLayout(): Array<Array<string>> {
        // not needed to render the `entity` field in editor because it will be duplicate, in list and as field...
        return entityRequirementEntityColumns.filter(column => column !== "entity").map(column => { return [column] });
    }

    protected renderEditorComponent(_: WithHW<ScriptableUiFieldEditor.Main>, __: ScriptableUiHighlightWrapper) {
        const entityRequirements = this.getEntityRequirements();
        return <div className="flex-container gap10">
            <EntityNameFieldEditor multiple={true} value={Object.keys(entityRequirements)} onChangeValue={values => this.updateEntityNames(values)} closeOnChange />
            {Object.keys(entityRequirements).map((entityName) => {
                return <Segment key={entityName} className="no-margin">
                    {entityDescriptors[entityName].getLabel()}
                    <EntityEditorFormSimple scriptableUiId={entityName} onSubmitHandler={(values) => this.updateGanttAssignmentEntityRequirement(entityName, values)}
                        entity={{ ...entityRequirements[entityName], entity: entityName }}
                        entityDescriptor={ganttAssignmentEntityRequirementEntityDescriptor}
                        hideButtonBar fieldsLayout={this.getFieldsLayout()}
                    />
                </Segment>
            })}
        </div>
    }
}

export class GanttAssignmentParams_ConfigEntityRequirementsJsonFieldDescriptor extends FieldDescriptor {

    constructor() {
        super();
        this.name = "configEntityRequirementsJson";
        this.filterable = false;
        this.sortable = false;
    }

    renderFieldEditor(formikProps: IFormValuesHolder<any>, _?: DummyToRememberPeopleToCast): ReactElement {
        return <GanttAssignmentParams_ConfigEntityRequirementsJsonFieldEditor formikProps={formikProps} />;
    }

    protected renderFieldInternal(_: any, __: FieldRendererProps, entity: any): ReactNode {
        let entityRequirements: { [key: string]: GanttAssignmentEntityRequirement } = {};

        if (entity.configEntityRequirementsJson) {
            entityRequirements = tryJsonParse(entity.configEntityRequirementsJson) ?? {};
        }

        let ganttAssignmentEntityRequirementEntity: GanttAssignmentEntityRequirement;
        let value: any;

        return <div className="flex-container gap3">
            {Object.keys(entityRequirements).map((entityName: string) => {
                ganttAssignmentEntityRequirementEntity = { ...entityRequirements[entityName], entity: entityName };
                return <Segment className="GanttAssignmentParams_configEntityRequirementsJson gap5 less-padding">
                    {entityRequirementEntityColumns.map(key => {
                        const fd = ganttAssignmentEntityRequirementEntityDescriptor.getField(key);
                        value = (ganttAssignmentEntityRequirementEntity as any)[key];
                        return Utils.isNullOrEmpty(value) ? null : <>
                            &nbsp;{fd.getLabel()}:&nbsp;
                            {fd.getFieldName() === "entity" ? <Label>{entityDescriptors[value].getLabel()}</Label> :
                                fd.renderField(fd.getType() === FieldType.filter ? { entity: entityName, [key]: JSON.stringify(value) } : ganttAssignmentEntityRequirementEntity, FieldDescriptor.castAdditionalFieldRendererProps(StringFieldRenderer, { asLabel: true }))}
                        </>;
                    })}
                </Segment>
            })}
        </div>
    }
}
